import { useEffect, useRef, useCallback } from "react";
import maplibregl from "maplibre-gl";
import { useSiteStore, ZOOM } from "../../stores/useSiteStore";
import { useProjectStore } from "../../stores/useProjectStore";
import { FEATURE_TYPE_COLOR_EXPR, SITE_TYPE_STYLES } from "../../constants/siteConstants";
import { SITEID_LAYERS } from "../../constants/mapLayerIds";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SOURCE_CLUSTERS = "siteid-sites-clusters";
const PULSE_SRC   = "siteid-pulse-src";
const PULSE_LAYER = "siteid-pulse-ring";  // centroid points → MapLibre clustering
const SOURCE_POLYS    = "siteid-sites-polys";     // bbox/simplified polygons z13+
const FEATURE_SRC     = "siteid-features";
const HIGHLIGHT_SRC   = "siteid-highlight-src";

// Layer ids live in constants/mapLayerIds.js (shared with MapView's
// click-priority list). Aliased back to LAYER so call sites are unchanged.
const LAYER = SITEID_LAYERS;

const FEATURE_LAYER = {
  FILL:    "siteid-feat-fill",
  OUTLINE: "siteid-feat-outline",
  LINE:    "siteid-feat-line",
  POINT:   "siteid-feat-point",
};

// ---------------------------------------------------------------------------
// Site type → fill color expression
// ---------------------------------------------------------------------------
// Site type colors — built from shared SITE_TYPE_STYLES constant
const SITE_TYPE_COLOR = [
  "match", ["get", "site_type"],
  ...Object.entries(SITE_TYPE_STYLES).flatMap(([k, v]) => [k, v.color]),
  "#6b7280",
];

// ---------------------------------------------------------------------------
// Feature colors imported from siteConstants
const FEATURE_TYPE_COLOR = FEATURE_TYPE_COLOR_EXPR;


// Membership role → opacity
const ROLE_OPACITY = [
  "match", ["get", "membership_role"],
  "anchor",    0.08,
  "component", 0.35,
  "context",   0.15,
  /* default */ 0.20,
];

const ROLE_LINE_WIDTH = [
  "match", ["get", "feature_class"],
  "road",         2.5,
  "fence_access", 2.5,
  "fence",        2.0,
  "pipeline",     3.0,
  [
    "match", ["get", "membership_role"],
    "anchor",    2.0,
    "component", 1.5,
    "context",   0.8,
    1.0,
  ],
];

const ROLE_POINT_RADIUS = [
  "match", ["get", "membership_role"],
  "anchor",    8,
  "component", 6,
  "context",   4,
  /* default */ 5,
];

const DEBOUNCE_MS = 180;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getViewportFromMap(map) {
  const bounds = map.getBounds();
  return {
    minLng: bounds.getWest(),
    minLat: bounds.getSouth(),
    maxLng: bounds.getEast(),
    maxLat: bounds.getNorth(),
    zoom:   Math.floor(map.getZoom()),
  };
}

function debounce(fn, ms) {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), ms); };
}

function safeRemoveLayer(map, id) {
  try { if (map.getLayer(id)) map.removeLayer(id); } catch (_) {}
}

function safeRemoveSource(map, id) {
  try { if (map.getSource(id)) map.removeSource(id); } catch (_) {}
}


// ---------------------------------------------------------------------------
// Pulse ring helpers
// ---------------------------------------------------------------------------
 
function getCentroid(geom) {
  if (!geom) return null;
  try {
    if (geom.type === "Point") return geom.coordinates;
    if (geom.type === "LineString") {
      const mid = Math.floor(geom.coordinates.length / 2);
      return geom.coordinates[mid];
    }
    if (geom.type === "MultiLineString") {
      const flat = geom.coordinates.flat();
      return flat[Math.floor(flat.length / 2)];
    }
    if (geom.type === "Polygon") {
      const coords = geom.coordinates[0];
      const lng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
      const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
      return [lng, lat];
    }
    if (geom.type === "MultiPolygon") {
      const coords = geom.coordinates[0][0];
      const lng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
      const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
      return [lng, lat];
    }
  } catch (_) {}
  return null;
}
 
function triggerPulse(map, center) {
  const SRC   = "siteid-pulse-src";
  const LAYER = "siteid-pulse-ring";
  const DURATION = 2000;  // ms
  const PULSES   = 2;
  const STEPS    = 60;
  const INTERVAL = DURATION / STEPS;
 
  const point = {
    type: "FeatureCollection",
    features: [{ type: "Feature", geometry: { type: "Point", coordinates: center } }],
  };
 
  // Add or update source
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: "geojson", data: point });
  } else {
    map.getSource(SRC).setData(point);
  }
 
  // Add layer if not present
  if (!map.getLayer(LAYER)) {
    map.addLayer({
      id: LAYER,
      type: "circle",
      source: SRC,
      paint: {
        "circle-radius":       0,
        "circle-color":        "transparent",
        "circle-stroke-width": 3,
        "circle-stroke-color": "#ff0080",
        "circle-stroke-opacity": 0,
        "circle-opacity":      0,
      },
    });
  }
 
  let step = 0;
  const tick = () => {
    if (!map.getLayer(LAYER)) return;
    const t = (step % STEPS) / STEPS;  // cycles 0→1 per pulse
 
    // Radius expands from 8 → 40px
    const radius = 10 + t * 45;
 
    // Opacity: rises quickly then fades out
    const opacity = t < 0.2
      ? t / 0.2           // fade in
      : 1 - (t - 0.2) / 0.8;  // fade out
 
    map.setPaintProperty(LAYER, "circle-radius",          radius);
    map.setPaintProperty(LAYER, "circle-stroke-opacity",  Math.max(0, opacity));
 
    step++;
    if (step <= STEPS * PULSES) {
      setTimeout(tick, INTERVAL);
    } else {
      // Clean up
      map.setPaintProperty(LAYER, "circle-stroke-opacity", 0);
      map.setPaintProperty(LAYER, "circle-radius", 0);
    }
  };
 
  // Small delay to let flyTo start first
  setTimeout(tick, 400);
}
 
// ---------------------------------------------------------------------------
// SiteLayer
// ---------------------------------------------------------------------------

export default function SiteLayer({ map }) {
  const fetchViewport       = useSiteStore(s => s.fetchViewport);
  const selectSite          = useSiteStore(s => s.selectSite);
  const siteCollection      = useSiteStore(s => s.siteCollection);
  const siteIdEnabled       = useSiteStore(s => s.siteIdEnabled);
  const selectedSiteId      = useSiteStore(s => s.selectedSiteId);
  const setActiveProject    = useSiteStore(s => s.setActiveProject);
  const siteActiveProjectId = useSiteStore(s => s.activeProjectId);
  const projectStoreId      = useProjectStore(s => s.activeProjectId);

  // Sync activeProjectId from useProjectStore → useSiteStore on mount
  // Handles direct navigation to AssetID when project is already selected
  useEffect(() => {
    if (projectStoreId && projectStoreId !== siteActiveProjectId) {
      setActiveProject(projectStoreId);
    }
  }, [projectStoreId, siteActiveProjectId, setActiveProject]);

  // Trigger initial fetch when siteId becomes enabled and map+project are ready
  useEffect(() => {
    if (!map || !siteIdEnabled || !initializedRef.current) return;
    const activeId = useSiteStore.getState().activeProjectId;
    if (!activeId) return;
    fetchViewport(getViewportFromMap(map));
  }, [siteIdEnabled, map]);
  const siteFeatures        = useSiteStore(s => s.siteFeatures);
  const selectedSiteDetail  = useSiteStore(s => s.selectedSiteDetail);
  const focusedFeatureId    = useSiteStore(s => s.focusedFeatureId);
  const clearFocusedFeature = useSiteStore(s => s.clearFocusedFeature);

  const initializedRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Register site source + layers
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || initializedRef.current) return;

    // Source 1: centroid points only — MapLibre clusters these at z≤12
    map.addSource(SOURCE_CLUSTERS, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
      cluster: true,
      clusterMaxZoom: ZOOM.CLUSTER_MAX,
      clusterRadius: 50,
      generateId: true,
    });

    // Source 2: bbox/poly features — receives polygon geometry at z13+
    // No clustering — MapLibre would convert polygons to points if cluster:true
    map.addSource(SOURCE_POLYS, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
      generateId: true,
    });

    // Cluster circles
    map.addLayer({
      id: LAYER.CLUSTERS,
      type: "circle",
      source: SOURCE_CLUSTERS,
      filter: ["has", "point_count"],
      paint: {
        "circle-radius": [
          "step", ["get", "point_count"],
          14, 10, 18, 50, 24, 200, 30,
        ],
        "circle-color":        "#f59e0b",
        "circle-opacity":      0.85,
        "circle-stroke-width": 1.5,
        "circle-stroke-color": "#ffffff",
      },
    });

    map.addLayer({
      id: LAYER.CLUSTER_COUNT,
      type: "symbol",
      source: SOURCE_CLUSTERS,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font":  ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size":  12,
      },
      paint: { "text-color": "#ffffff" },
    });

    // Polygons from z13+ — bbox at z13-14, simplified poly at z15+
    // Uses separate source (no cluster:true) so polygon geometry is preserved
    map.addLayer({
      id: LAYER.POLYGONS,
      type: "fill",
      source: SOURCE_POLYS,
      filter: ["!", ["has", "point_count"]],
      minzoom: ZOOM.CLUSTER_MAX + 1,
      paint: {
        "fill-color":   SITE_TYPE_COLOR,
        "fill-opacity": [
          "interpolate", ["linear"], ["zoom"],
          13, 0.03,
          15, 0.06,
        ],
      },
    });

    map.addLayer({
      id: LAYER.POLYGONS_OUTLINE,
      type: "line",
      source: SOURCE_POLYS,
      filter: ["!", ["has", "point_count"]],
      minzoom: ZOOM.CLUSTER_MAX + 1,
      paint: {
        "line-color":   SITE_TYPE_COLOR,
        "line-width": [
          "interpolate", ["linear"], ["zoom"],
          13, 1.0,
          15, 1.8,
          17, 2.5,
        ],
        "line-opacity": 0.9,
      },
    });

    initializedRef.current = true;

    return () => {
      if (!map?.getStyle?.()) { initializedRef.current = false; return; }
      Object.values(LAYER).forEach(id => safeRemoveLayer(map, id));
      // Selection overlays are added in separate effects with no per-effect
      // cleanup, so tear them down here too — otherwise they linger on the map
      // after leaving AssetID mode.
      safeRemoveLayer(map, "siteid-highlight-fill");
      safeRemoveLayer(map, "siteid-highlight-line");
      Object.values(FEATURE_LAYER).forEach(id => safeRemoveLayer(map, id));
      safeRemoveLayer(map, PULSE_LAYER);
      safeRemoveSource(map, SOURCE_CLUSTERS);
      safeRemoveSource(map, SOURCE_POLYS);
      safeRemoveSource(map, HIGHLIGHT_SRC);
      safeRemoveSource(map, FEATURE_SRC);
      safeRemoveSource(map, PULSE_SRC);
      initializedRef.current = false;
    };
  }, [map]);

  // ---------------------------------------------------------------------------
  // Viewport fetch
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map) return;
    const handleMove = debounce(() => {
      if (!siteIdEnabled) return;
      fetchViewport(getViewportFromMap(map));
    }, DEBOUNCE_MS);

    map.on("moveend", handleMove);
    map.on("zoomend", handleMove);
    if (siteIdEnabled) fetchViewport(getViewportFromMap(map));

    return () => {
      map.off("moveend", handleMove);
      map.off("zoomend", handleMove);
    };
  }, [map, siteIdEnabled, fetchViewport]);

  // ---------------------------------------------------------------------------
  // Sync site GeoJSON — two sources, same collection
  //
  // At z≤12 the server returns centroid points → SOURCE_CLUSTERS handles clustering
  // At z13+ the server returns bbox/poly geometry → SOURCE_POLYS renders fills
  // Both sources receive the same siteCollection — the server controls geometry
  // via the zoom param, and each source only renders its zoom range.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;
    map.getSource(SOURCE_CLUSTERS)?.setData(siteCollection);
    map.getSource(SOURCE_POLYS)?.setData(siteCollection);
  }, [map, siteCollection]);

  // ---------------------------------------------------------------------------
  // Visibility toggle
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;
    const vis = siteIdEnabled ? "visible" : "none";
    Object.values(LAYER).forEach(id => {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
    });
    // Also hide/show poly source layers (not in LAYER enum separately)
  }, [map, siteIdEnabled]);

  // ---------------------------------------------------------------------------
  // Selection highlight — outline
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;
    const sel = selectedSiteId ?? "";

    if (map.getLayer(LAYER.POLYGONS_OUTLINE)) {
      map.setPaintProperty(LAYER.POLYGONS_OUTLINE, "line-width", [
        "interpolate", ["linear"], ["zoom"],
        13, ["case", ["==", ["get", "id"], sel], 2.5, 1.0],
        15, ["case", ["==", ["get", "id"], sel], 3.5, 1.8],
        17, ["case", ["==", ["get", "id"], sel], 4.0, 2.5],
      ]);
    }
  }, [map, selectedSiteId]);

  // ---------------------------------------------------------------------------
  // Fit bounds on selection
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !selectedSiteDetail?.bbox) return;
    const ring = selectedSiteDetail.bbox.coordinates[0];
    const bounds = ring.reduce(
      (b, c) => b.extend(c),
      new maplibregl.LngLatBounds(ring[0], ring[0])
    );
    map.fitBounds(bounds, { padding: 80, duration: 800, maxZoom: 17 });
  }, [map, selectedSiteDetail]);

  // ---------------------------------------------------------------------------
  // Selected site highlight overlay
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map) return;

    const removeHighlight = () => {
      safeRemoveLayer(map, "siteid-highlight-fill");
      safeRemoveLayer(map, "siteid-highlight-line");
      safeRemoveSource(map, HIGHLIGHT_SRC);
    };

    // No selection → tear the overlay down (e.g. after clearSelection()).
    if (!selectedSiteDetail?.geom) {
      removeHighlight();
      return;
    }

    const fc = {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: selectedSiteDetail.geom, properties: {} }],
    };

    const apply = () => {
      if (!map.getSource(HIGHLIGHT_SRC)) {
        map.addSource(HIGHLIGHT_SRC, { type: "geojson", data: fc });
        map.addLayer({
          id: "siteid-highlight-fill", type: "fill", source: HIGHLIGHT_SRC,
          paint: { "fill-color": "#22c55e", "fill-opacity": 0.0 },
        });
        map.addLayer({
          id: "siteid-highlight-line", type: "line", source: HIGHLIGHT_SRC,
          paint: { "line-color": "#22c55e", "line-width": 2.5, "line-dasharray": [3, 2] },
        });
      } else {
        map.getSource(HIGHLIGHT_SRC).setData(fc);
      }
    };

    map.isStyleLoaded() ? apply() : map.once("idle", apply);
  }, [map, selectedSiteDetail]);

  // ---------------------------------------------------------------------------
  // Site features overlay — geometry-type aware
  // Uses new membership roles: anchor / component / context
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map) return;

    const removeFeatures = () => {
      Object.values(FEATURE_LAYER).forEach(id => safeRemoveLayer(map, id));
      safeRemoveSource(map, FEATURE_SRC);
    };

    // No selection → tear the feature overlay down (e.g. after clearSelection()).
    if (!siteFeatures) {
      removeFeatures();
      return;
    }

    const apply = () => {
      if (!map.getSource(FEATURE_SRC)) {
        map.addSource(FEATURE_SRC, { type: "geojson", data: siteFeatures });
      } else {
        map.getSource(FEATURE_SRC).setData(siteFeatures);
        // Source already exists — layers already added, just data update needed
        return;
      }

      // Polygon fill
      map.addLayer({
        id: FEATURE_LAYER.FILL,
        type: "fill",
        source: FEATURE_SRC,
        filter: ["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]],
        paint: {
          "fill-color":   FEATURE_TYPE_COLOR,
          "fill-opacity": ROLE_OPACITY,
        },
      });

      // Polygon outline
      map.addLayer({
        id: FEATURE_LAYER.OUTLINE,
        type: "line",
        source: FEATURE_SRC,
        filter: ["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]],
        paint: {
          "line-color":   FEATURE_TYPE_COLOR,
          "line-width":   ROLE_LINE_WIDTH,
          "line-opacity": 0.9,
        },
      });

      // LineString
      map.addLayer({
        id: FEATURE_LAYER.LINE,
        type: "line",
        source: FEATURE_SRC,
        filter: ["in", ["geometry-type"], ["literal", ["LineString", "MultiLineString"]]],
        paint: {
          "line-color":     FEATURE_TYPE_COLOR,
          "line-width":     ROLE_LINE_WIDTH,
          "line-opacity":   [
            "match", ["get", "membership_role"],
            "anchor", 0.9, "component", 0.7, "context", 0.4, 0.6,
          ],
          "line-dasharray": [
            "match", ["get", "feature_class"],
            "pipeline",    ["literal", [4, 2]],
            "road",        ["literal", [1]],
            "fence_access",["literal", [2, 1]],
                           ["literal", [1]],
          ],
        },
      });

      // Points
      map.addLayer({
        id: FEATURE_LAYER.POINT,
        type: "circle",
        source: FEATURE_SRC,
        filter: ["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]],
        paint: {
          "circle-radius":       ROLE_POINT_RADIUS,
          "circle-color":        FEATURE_TYPE_COLOR,
          "circle-opacity":      [
            "match", ["get", "membership_role"],
            "anchor", 0.95, "component", 0.80, "context", 0.50, 0.65,
          ],
          "circle-stroke-width": [
            "match", ["get", "membership_role"],
            "anchor", 2, "component", 1, 0,
          ],
          "circle-stroke-color": "#0f172a",
        },
      });
    };

    map.isStyleLoaded() ? apply() : map.once("idle", apply);
  }, [map, siteFeatures]);

  // ---------------------------------------------------------------------------
  // Click handlers
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return;

    const handleFeatureClick = e => {
      const f = e.features?.[0];
      if (f) selectSite(f.properties.id);
    };

    const handleClusterClick = e => {
      const f = e.features?.[0];
      if (!f) return;
      map.getSource(SOURCE_CLUSTERS).getClusterExpansionZoom(
        f.properties.cluster_id,
        (err, zoom) => { if (!err) map.easeTo({ center: e.lngLat, zoom: zoom + 0.5 }); }
      );
    };

    const setPointer  = () => { map.getCanvas().style.cursor = "pointer"; };
    const resetCursor = () => { map.getCanvas().style.cursor = ""; };

    map.on("click",      LAYER.POLYGONS,  handleFeatureClick);
    map.on("click",      LAYER.CLUSTERS,  handleClusterClick);
    map.on("mouseenter", LAYER.POLYGONS,  setPointer);
    map.on("mouseleave", LAYER.POLYGONS,  resetCursor);
    map.on("mouseenter", LAYER.CLUSTERS,  setPointer);
    map.on("mouseleave", LAYER.CLUSTERS,  resetCursor);

    return () => {
      map.off("click",      LAYER.POLYGONS,  handleFeatureClick);
      map.off("click",      LAYER.CLUSTERS,  handleClusterClick);
      map.off("mouseenter", LAYER.POLYGONS,  setPointer);
      map.off("mouseleave", LAYER.POLYGONS,  resetCursor);
      map.off("mouseenter", LAYER.CLUSTERS,  setPointer);
      map.off("mouseleave", LAYER.CLUSTERS,  resetCursor);
    };
  }, [map, selectSite]);


  // ---------------------------------------------------------------------------
  // Fly to focused feature when selected from panel
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !focusedFeatureId || !siteFeatures) return;
 
    const feature = siteFeatures.features?.find(f => f.id === focusedFeatureId);
    if (!feature?.geometry) return;
 
    const geom = feature.geometry;
 
    // Compute bounds from geometry
    let coords = [];
    if (geom.type === "Point") {
      coords = [geom.coordinates];
    } else if (geom.type === "LineString") {
      coords = geom.coordinates;
    } else if (geom.type === "MultiLineString") {
      coords = geom.coordinates.flat();
    } else if (geom.type === "Polygon") {
      coords = geom.coordinates[0];
    } else if (geom.type === "MultiPolygon") {
      coords = geom.coordinates.flat(2);
    }
 
    if (!coords.length) return;
 
    if (coords.length === 1) {
      // Single point — flyTo with fixed zoom
      map.flyTo({
        center: coords[0],
        zoom: Math.max(map.getZoom(), 17),
        duration: 600,
      });
    } else {
      // Multi-coord — fitBounds
      const bounds = coords.reduce(
        (b, c) => b.extend(c),
        new maplibregl.LngLatBounds(coords[0], coords[0])
      );
      map.fitBounds(bounds, { padding: 120, duration: 600, maxZoom: 19 });
    }
 
    clearFocusedFeature();
 
    // Trigger pulse ring at feature centroid
    const centroid = getCentroid(geom);
    if (centroid) triggerPulse(map, centroid);
 
  }, [map, focusedFeatureId, siteFeatures, clearFocusedFeature]);
 
  return null;
}
